import { Button, Col, List, Row, Typography } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { CaretSpinIcon } from 'components/atoms/CaretSpinIcon';
import { UserCard } from 'components/atoms/UserCard';

import { Fragment, LegacyRef, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import { useMeasure } from 'react-use';
import { cookieMonsterApi } from 'redux/services/cookieMonster/cookieMonsterApi';
import { mrsGrouchApi, useGetProjectQuery, useGetProjectUsersQuery } from 'redux/services/mrsGrouch/mrsGrouchApi';
import { AddUserModal } from './AddUserModal';

export const UsersList: React.FC = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const { divisionId, projectId } = useParams<{ divisionId: string; projectId: string }>();
  const { data: projectData } = useGetProjectQuery({ divisionId: divisionId as string, projectId: projectId as string }, { skip: !divisionId || !projectId });
  const dispatch = useDispatch();

  const { data: usersData, isLoading, isFetching } = useGetProjectUsersQuery({ divisionId: divisionId as string, projectId: projectId as string }, { skip: !divisionId || !projectId });

  useEffect(() => {
    if (usersData?.data?.length) {
      setShowUsers(true);
    }
  }, [usersData?.data?.length]);

  const handleToggleShowUsers = useCallback((ev: MouseEvent<HTMLElement>): void => {
    ev.preventDefault();
    ev.stopPropagation();
    setShowUsers((prevState) => !prevState);
  }, []);

  const handleClick = useCallback((ev: MouseEvent<HTMLElement, globalThis.MouseEvent>): void => {
    ev.stopPropagation();
    setIsOpen(true);
  }, []);

  const handleUserRemoved = useCallback((): void => {
    dispatch(cookieMonsterApi.util.invalidateTags(['Users']));
    dispatch(mrsGrouchApi.util.invalidateTags(['Project']));
  }, [dispatch]);

  const [ref, { height }] = useMeasure();

  const expandAnimation = useSpring({
    config: { friction: 12 },
    height: showUsers ? height : 0
  });

  const usersList = useMemo(
    () => (
      <List
        dataSource={usersData?.data || []}
        loading={{ indicator: <BlockLoader direction="loader loader--slideUp" />, spinning: isLoading || isFetching }}
        grid={{ gutter: 4, column: 4, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 4 }}
        style={{ width: '100%' }}
        renderItem={(item): React.ReactNode => (
          <Col>
            <UserCard user={item} currentProject={projectData!} onUserRemoved={handleUserRemoved} />
          </Col>
        )}
      />
    ),
    [usersData?.data, isLoading, isFetching, handleUserRemoved, projectData]
  );

  const usersListJSX = useMemo(
    () => (
      <Row>
        <Col span={24}>{usersList}</Col>
      </Row>
    ),
    [usersList]
  );

  return (
    <Fragment>
      {projectData && <AddUserModal isOpen={isOpen} setIsOpen={setIsOpen} projectId={projectId as string} currentProject={projectData} />}
      <Col span={24} style={{ marginBottom: showUsers ? 10 : 0 }}>
        <Row onClick={handleToggleShowUsers} style={{ padding: 15, background: 'rgba(24, 167, 153, 0.518)', borderRadius: 5, cursor: 'pointer' }} justify="space-between">
          <Typography.Title style={{ fontWeight: 400, padding: 0, margin: 0, fontSize: 22 }} level={3}>
            Users {usersData?.data?.length ? `(${usersData.data.length})` : ''}
          </Typography.Title>
          <Row>
            <Col>
              <Button onClick={handleClick}>Add Users</Button>
            </Col>
            <Col>
              <Button style={{ width: 115 }} type="text" onClick={handleToggleShowUsers} key="show-users" icon={<CaretSpinIcon isOpen={showUsers} />}>
                {showUsers ? 'Collapse' : 'Expand'}
              </Button>
            </Col>
          </Row>
        </Row>
        <Row style={{ background: 'transparent', padding: '0 1.25px' }}>
          <animated.div style={{ ...{ minHeight: 10, overflow: 'hidden', width: '100%' }, ...expandAnimation }}>
            {showUsers && (
              <div ref={ref as LegacyRef<HTMLDivElement>} style={{ width: '100%' }}>
                {usersListJSX}
              </div>
            )}
          </animated.div>
        </Row>
      </Col>
    </Fragment>
  );
};
